import type { CSSObject } from '@emotion/react'
import {
  Color,
  FocusIndicator,
  OppositeTheme,
  Size,
  Spacing,
  Typography,
} from '@opallabs/gem'
import type { WithRequiredTheme } from '@opallabs/lapidary'

type StyleProps = Readonly<
  WithRequiredTheme & {
    disabled: boolean
  }
>

export const icon = (type: string): CSSObject | null => {
  if (type === 'button') {
    return {
      marginInlineEnd: Spacing.S,
    }
  }
  if (type === 'image-preview') {
    return {
      alignItems: 'center',
      display: 'flex',
      blockSize: '100%',
      justifyContent: 'center',
      inlineSize: Spacing.XL * 1.25,
    }
  }
  return null
}

// Approximates the appearance of Gem button.
export const uploader = ({ disabled, theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N300,
  color: Color[theme].Text.Default,
  ...Typography.T500,
  alignItems: 'center',
  blockSize: Size.ControlHeight,
  borderColor: 'transparent',
  borderRadius: Size.Rounding,
  borderStyle: 'solid',
  borderWidth: 1,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  letterSpacing: 'inherit',
  opacity: disabled ? 0.5 : 1,
  paddingInline: Spacing.S * 1.25,
  position: 'relative',
  whiteSpace: 'nowrap',

  // used for hover/active overlays on top of the button’s base color
  '&::before': {
    borderRadius: Size.Rounding,
    content: '""',
    display: 'flex',
    // `-1` compensates for `borderWidth: 1`; without it `::before` is inset
    // by 1 pixel from the button’s outer edge
    inset: -1,
    position: 'absolute',
    zIndex: 0,
  },

  '&:focus-within': disabled
    ? null
    : {
        ...FocusIndicator[theme],
      },

  '&:focus-within, &:hover': disabled
    ? null
    : {
        '&::before': {
          backgroundColor: Color[OppositeTheme[theme]].Translucent.N12,
        },
      },
  '&:active': disabled
    ? null
    : {
        '&::before': {
          backgroundColor: Color[OppositeTheme[theme]].Translucent.N24,
        },
      },

  // Make sure the invisible `<input>` doesn't go outside the uploader
  // container.
  input: {
    maxInlineSize: '100%',
  },
})
